import React, { useEffect, useRef, useState } from "react";
import { Box, Card, Typography, CardContent, Grid, Backdrop, TextField } from "@material-ui/core";
import moment from "moment";
import "moment-timezone";
import { filterBy } from "src/common/utils/helpers";
import TabHeader from "src/components/TabHeader/TabHeader";
import { useStyles } from "./style";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import { useFormik } from "formik";
import { object, string } from "yup";
import { GridRowModel } from "@material-ui/x-grid";
import CommonButton from "src/components/CommonButton/CommonButton";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import CommonLoader from "src/components/Loader/CommonLoader";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { filteryByArr } from "src/common/utils/app.dropdowns";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { useDispatch } from "react-redux";

interface Props {}

const formatDate = (value: any) => {
  if (!value) return "";
  const m = moment(value);
  if (!m.isValid() || m.year() <= 1) return "";
  return m.format("DD-MM-YYYY");
};

export function CargoReports({}: Props): React.ReactElement {
  const columns = [
    {
      accessorKey: "virNumber",
      header: "VIR Number",
      type: "string",
      size: 180,
      accessorFn: (row: any) => ` ${row.virNo || row.virNumber || " "}`,
    },
    {
      accessorKey: "containerNumber",
      header: "Container Number",
      type: "string",
      size: 180,
      accessorFn: (row: any) => ` ${row.containerNo || row.containerNumber || " "}`,
    },
    {
      accessorKey: "blNo",
      header: "BL No",
      type: "string",
      size: 150,
      accessorFn: (row: any) => ` ${row.billNumber || row.blNo || row.blNumber || " "}`,
    },
    {
      accessorKey: "indexNo",
      header: "Index No",
      type: "string",
      size: 150,
      accessorFn: (row: any) =>
        ` ${row.indexNo !== undefined && row.indexNo !== null ? row.indexNo : " "}`,
    },
    {
      accessorKey: "manifestedQty",
      header: "Manifested QTY",
      type: "string",
      size: 150,
      accessorFn: (row: any) =>
        ` ${
          row.manifestedQty !== undefined && row.manifestedQty !== null
            ? row.manifestedQty
            : row.manifestedQTY ?? " "
        }`,
    },
    {
      accessorKey: "goodsDesc",
      header: "Goods Desc",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.goodsDescription || row.goodsDesc || " "}`,
    },
    {
      accessorKey: "crNo",
      header: "CR No",
      type: "string",
      size: 150,
      accessorFn: (row: any) => ` ${row.crNumber || row.crNo || " "}`,
    },
    {
      accessorKey: "doNumber",
      header: "DO Number",
      type: "string",
      size: 150,
      accessorFn: (row: any) => ` ${row.doNumber || " "}`,
    },
    {
      accessorKey: "vesselArrivalDate",
      header: "Vessel Arrival Date",
      type: "string",
      size: 180,
      accessorFn: (row: any) => ` ${formatDate(row.vesselArrivalDate)}`,
    },
    {
      accessorKey: "line",
      header: "Line",
      type: "string",
      size: 150,
      accessorFn: (row: any) => ` ${row.line || " "}`,
    },
    {
      accessorKey: "vesselName",
      header: "Vessel Name",
      type: "string",
      size: 180,
      accessorFn: (row: any) => ` ${row.vesselName || " "}`,
    },
    {
      accessorKey: "voyageNo",
      header: "Voyage No",
      type: "string",
      size: 150,
      accessorFn: (row: any) => ` ${row.voyageNo || " "}`,
    },
    {
      accessorKey: "igmDate",
      header: "IGM Date",
      type: "string",
      size: 150,
      accessorFn: (row: any) => ` ${formatDate(row.igmDate)}`,
    },
    {
      accessorKey: "arrivalInAictDate",
      header: "Arrival In ODT Date",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${formatDate(row.arrivalInAICT || row.arrivalInAictDate)}`,
    },
    {
      accessorKey: "truckNo",
      header: "Vehicle No",
      type: "string",
      size: 150,
      accessorFn: (row: any) => ` ${row.truckNo || " "}`,
    },
    {
      accessorKey: "cfsCy",
      header: "CFS/CY",
      type: "string",
      size: 120,
      accessorFn: (row: any) => ` ${row.igmStatus || row.cfsCy || row.cfsCY || " "}`,
    },
  ];

  const classes = useStyles();
  const dispatch = useDispatch();
  const moduleName = "Cargo Reports";
  const pageHeading = "Cargo Reports";
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [gridRecord, setGridRecord] = React.useState<GridRowModel[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [isCustomDate, setIsCustomDate] = useState<boolean>(false);
  const [virOptions, setVirOptions] = useState<any[]>([]);
  const [containerOptions, setContainerOptions] = useState<any[]>([]);
  const [blOptions, setBlOptions] = useState<any[]>([]);
  const [virInputLength, setVirInputLength] = useState(0);
  const [containerInputLength, setContainerInputLength] = useState(0);
  const [blInputLength, setBlInputLength] = useState(0);
  const startDatePickerRef: any = useRef(null);
  const endDatePickerRef: any = useRef(null);
  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";

  const validationSchema = object({
    startDate: string().required("This field is required."),
    endDate: string().required("This field is required."),
  });

  const formik = useFormik({
    initialValues: {
      filterBy: "today",
      startDate: moment().startOf("day").format("YYYY-MM-DDTHH:mm"),
      endDate: moment().endOf("day").format("YYYY-MM-DDTHH:mm"),
      virNumber: "",
      containerNumber: "",
      blNumber: "",
      indexNumber: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const body = {
        startDate: values.startDate || null,
        endDate: values.endDate || null,
        virNo: values.virNumber?.trim() || null,
        containerNo: values.containerNumber?.trim() || null,
        billNumber: values.blNumber?.trim() || null,
        indexNo: values.indexNumber?.trim() || null,
      };
      setGridRecord([]);
      setLoader(true);
      try {
        const results = await fetchWrapper.post("get-cargo-report", body);
        setGridRecord(results?.value?.cargoReports || results?.value?.report || []);
        setDisplayColumns(columns);
      } catch (error: any) {
        dispatch(
          addNotificationAction({
            message: error.message || "Error fetching cargo report data",
            options: { variant: "error" },
          })
        );
      }
      setLoader(false);
    },
  });

  useEffect(() => {
    formik.submitForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportHandler = async (rows: any[]) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );

    return rows.map((data: any) => ({
      "VIR Number": data?.virNo || data?.virNumber || "",
      "Container Number": data?.containerNo || data?.containerNumber || "",
      "BL No": data?.billNumber || data?.blNo || data?.blNumber || "",
      "Index No": data?.indexNo ?? "",
      "Manifested QTY": data?.manifestedQty ?? data?.manifestedQTY ?? "",
      "Goods Desc": data?.goodsDescription || data?.goodsDesc || "",
      "CR No": data?.crNumber || data?.crNo || "",
      "DO Number": data?.doNumber || "",
      "Vessel Arrival Date": formatDate(data?.vesselArrivalDate),
      Line: data?.line || "",
      "Vessel Name": data?.vesselName || "",
      "Voyage No": data?.voyageNo || "",
      "IGM Date": formatDate(data?.igmDate),
      "Arrival In AICT Date": formatDate(data?.arrivalInAICT || data?.arrivalInAictDate),
      "Truck No": data?.truckNo || "",
      "CFS/CY": data?.igmStatus || data?.cfsCy || data?.cfsCY || "",
    }));
  };

  const filterByAction = (data: any) => {
    formik.setFieldValue("filterBy", data.value);
    if (data && data.value) {
      const res = filterBy(data);
      if (res?.isCustomDate) {
        setIsCustomDate(true);
        formik.setFieldValue("startDateCustom", res?.startDate);
        formik.setFieldValue("endDateCustom", res?.endDate);
      } else {
        setIsCustomDate(false);
      }
      formik.setFieldValue("startDate", res?.startDate);
      formik.setFieldValue("endDate", res?.endDate);
    }
  };

  const getVirNumbers = async (virNo: string) => {
    const trimmed = String(virNo ?? "").trim();
    if (!trimmed) return;
    const results: any = await fetchWrapper.get(`search-vir-number/${trimmed}`);
    if (results?.isSuccess) setVirOptions(results.value?.virList ?? []);
  };

  const getContainerNumbers = async (containerNo: string) => {
    const trimmed = String(containerNo ?? "").trim();
    if (!trimmed) return;
    const results: any = await fetchWrapper.get(`search-container-number/${trimmed}`);
    if (results?.isSuccess) setContainerOptions(results.value?.containerList ?? []);
  };

  const getBlNumbers = async (blNo: string) => {
    const trimmed = String(blNo ?? "").trim();
    if (!trimmed) return;
    const results: any = await fetchWrapper.get(`search-bl-number/${trimmed}`);
    if (results?.isSuccess) setBlOptions(results.value?.billNumberList ?? []);
  };

  const resetSearch = () => {
    formik.resetForm();
    setIsCustomDate(false);
    setGridRecord([]);
    setVirOptions([]);
    setContainerOptions([]);
    setBlOptions([]);
    setVirInputLength(0);
    setContainerInputLength(0);
    setBlInputLength(0);
    formik.setFieldValue("filterBy", "today");
    formik.setFieldValue("startDate", moment().startOf("day").format("YYYY-MM-DDTHH:mm"));
    formik.setFieldValue("endDate", moment().endOf("day").format("YYYY-MM-DDTHH:mm"));
  };

  const textFieldProps = {
    size: "small" as const,
    fullWidth: true,
    className: "customTextField",
    variant: "outlined" as const,
    InputLabelProps: { shrink: true },
  };

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>

      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2}>
                <Typography className={classes.pageHeading}>{pageHeading}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3} direction="row">
              <Box textAlign="right" mt={1} mb={1}>
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetSearch()}>
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  onClick={() => formik.submitForm()}
                >
                  Search
                </CommonButton>
              </Box>
            </Grid>
          </Grid>

          <Grid spacing={1} container direction="row">
            <Grid item xs={12} md={3} lg={2}>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                error={formik.touched.filterBy && Boolean(formik.errors.filterBy)}
                helperText={formik.touched.filterBy && formik.errors.filterBy}
                label="Filter by"
                placeholder="Filter by"
                name="filterBy"
                disableClearable={true}
                optionsType={filteryByArr}
                getOptionLabel={(option: any) => option.label || null}
                onChange={(row: any) => {
                  row ? formik.setValues({ ...formik.values, filterBy: row.value || "" }) : "";
                  if (row === null) {
                    formik.setFieldValue("filterBy", null);
                  }
                  filterByAction(row);
                }}
                value={
                  filteryByArr.find(({ value }: any) => value === formik.values.filterBy) || ""
                }
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2} className={!isCustomDate ? classes.hideDatesGrid : ""}>
              <CommonTextFieldDate
                cssClassName="customTextField"
                label={"Start Date"}
                name={"startDate"}
                datePickerRef={startDatePickerRef}
                value={formik.values.startDate}
                onChange={(e: any) => {
                  formik.setFieldValue("startDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                  formik.setFieldValue("endDate", "");
                }}
                maxDate={moment().format("YYYY-MM-DDTHH:mm")}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2} className={!isCustomDate ? classes.hideDatesGrid : ""}>
              <CommonTextFieldDate
                cssClassName="customTextField"
                label={"End Date"}
                name={"endDate"}
                datePickerRef={endDatePickerRef}
                value={formik.values.endDate}
                onChange={(e: any) => {
                  formik.setFieldValue("endDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                }}
                minDate={formik.values.startDate}
                maxDate={moment().format("YYYY-MM-DDTHH:mm")}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2}>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                label="VIR Number"
                placeholder="Search VIR Number"
                name="virNumber"
                optionsType={virOptions}
                getOptionLabel={(option: any) => option?.virNo || ""}
                value={virOptions.find((x: any) => x?.virNo === formik.values.virNumber) || null}
                handleChange={(v: string) => formik.setFieldValue("virNumber", v)}
                onChange={(option: any) => {
                  formik.setFieldValue("virNumber", option?.virNo || "");
                }}
                onKeyUp={(e: any) => {
                  const nextLen = e.target.value.length;
                  setVirInputLength(nextLen);
                  if (nextLen === 2 && virInputLength < 2) getVirNumbers(e.target.value);
                }}
                onKeyDownCapture={(e: any) => {
                  if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                }}
                onPaste={(e: any) => {
                  const pasted = e.clipboardData.getData("text");
                  if (/[^A-Za-z0-9\d]/.test(pasted)) {
                    e.preventDefault();
                    return;
                  }
                  if (pasted.length >= 2) getVirNumbers(pasted);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2}>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                label="Container Number"
                placeholder="Search Container Number"
                name="containerNumber"
                optionsType={containerOptions}
                getOptionLabel={(option: any) =>
                  option?.containerNo || option?.containerNumber || ""
                }
                value={
                  containerOptions.find(
                    (x: any) =>
                      (x?.containerNo || x?.containerNumber) === formik.values.containerNumber
                  ) || null
                }
                handleChange={(v: string) =>
                  formik.setFieldValue("containerNumber", String(v || "").toUpperCase())
                }
                onChange={(option: any) => {
                  formik.setFieldValue(
                    "containerNumber",
                    String(option?.containerNo || option?.containerNumber || "").toUpperCase()
                  );
                }}
                onKeyUp={(e: any) => {
                  const nextLen = e.target.value.length;
                  setContainerInputLength(nextLen);
                  if (nextLen === 2 && containerInputLength < 2) {
                    getContainerNumbers(e.target.value);
                  }
                }}
                onKeyDownCapture={(e: any) => {
                  if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                }}
                onPaste={(e: any) => {
                  const pasted = e.clipboardData.getData("text");
                  if (/[^A-Za-z0-9\d]/.test(pasted)) {
                    e.preventDefault();
                    return;
                  }
                  if (pasted.length >= 2) getContainerNumbers(pasted);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2}>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                label="BL Number"
                placeholder="Search BL Number"
                name="blNumber"
                optionsType={blOptions}
                getOptionLabel={(option: any) =>
                  option?.billNumber || option?.blNo || option?.blNumber || ""
                }
                value={
                  blOptions.find(
                    (x: any) => (x?.billNumber || x?.blNo || x?.blNumber) === formik.values.blNumber
                  ) || null
                }
                handleChange={(v: string) => formik.setFieldValue("blNumber", v)}
                onChange={(option: any) => {
                  formik.setFieldValue(
                    "blNumber",
                    option?.billNumber || option?.blNo || option?.blNumber || ""
                  );
                }}
                onKeyUp={(e: any) => {
                  const nextLen = e.target.value.length;
                  setBlInputLength(nextLen);
                  if (nextLen === 2 && blInputLength < 2) getBlNumbers(e.target.value);
                }}
                onPaste={(e: any) => {
                  const pasted = e.clipboardData.getData("text");
                  if (pasted.length >= 2) getBlNumbers(pasted);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2}>
              <TextField
                {...textFieldProps}
                label="Index Number"
                placeholder="Enter Index Number"
                name="indexNumber"
                value={formik.values.indexNumber}
                onChange={(e) =>
                  formik.setFieldValue("indexNumber", e.target.value.replace(/\D/g, ""))
                }
                onKeyDownCapture={(e: any) => {
                  const allowedKeys = [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "ArrowLeft",
                    "ArrowRight",
                    "Home",
                    "End",
                  ];
                  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
                  if (/[^\d]/.test(e.key)) e.preventDefault();
                }}
                onPaste={(e: any) => {
                  e.preventDefault();
                  const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
                  formik.setFieldValue("indexNumber", pasted);
                }}
              />
            </Grid>
          </Grid>

          <CommonMaterialGrid
            rows={gridRecord}
            columns={displayColumns}
            defaultPerPageRecord={50}
            loader={loader}
            height={600}
            isExport={isExport}
            exportHandler={exportHandler}
            filename={`Cargo_report_${Date.now()}`}
            isFooter={true}
          />
        </CardContent>
      </Card>
    </>
  );
}
